import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useRef } from 'react';
import { FaLock, FaShieldAlt, FaHandsHelping } from 'react-icons/fa';
import { MdSecurity, MdShowChart, MdSpeed } from 'react-icons/md';
import './P2PLending.css';

const P2PLending = () => {
  const [loanAmount,    setLoanAmount]    = useState(50000);
  const [repaymentTerm, setRepaymentTerm] = useState(8);
  const [interestRate,  setInterestRate]  = useState(12);

  const p2pRef = useRef(null);

  const calculateEMI = () => {
    const r = interestRate / 12 / 100;
    const emi = loanAmount * r * Math.pow(1 + r, repaymentTerm) /
                (Math.pow(1 + r, repaymentTerm) - 1);
    return Math.round(emi);
  };

  const benefits = [
    { icon: <FaLock />,       title: "Secure Escrow",     desc: "Funds held in escrow until loan disbursal" },
    { icon: <MdSecurity />,   title: "Verified Lenders",  desc: "All lenders undergo thorough KYC verification" },
    { icon: <MdShowChart />,  title: "Better Rates",      desc: "Lower interest rates than traditional lenders" },
    { icon: <FaHandsHelping />, title: "Friends First",   desc: "Your connections get priority in funding" },
    { icon: <FaShieldAlt />,  title: "Default Protection", desc: "Insurance-backed protection against defaults" },
    { icon: <MdSpeed />,      title: "Fast Processing",   desc: "Loan approval within 24-48 hours" }
  ];

  const comparisons = [
    { name: 'Rupayya P2P',  rate: '12-20%', desc: 'Based on social credit score' },
    { name: 'NBFCs',         rate: '15-24%', desc: 'High processing fees' },
    { name: 'Loan Apps',     rate: '18-36%', desc: 'Hidden charges apply' },
    { name: 'Local Lender',  rate: '36-60%', desc: 'Daily payment' }
  ];

  const steps = [
    { number: '1', title: 'Check Eligibility', desc: 'Verify your Rupayya Score and requirements' },
    { number: '2', title: 'Friends First',      desc: 'Your friends get priority to fund your loan' },
    { number: '3', title: 'P2P Marketplace',    desc: 'Remaining amount funded by verified lenders' },
    { number: '4', title: 'Secure Repayment',   desc: 'Flexible EMI options with auto-debit' }
  ];

  return (
    <section className="p2p-lending-section" id="p2p-lending" ref={p2pRef}>
      <div className="container">

        {/* Section header */}
        <div className="section-header">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            P2P Lending Marketplace
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Access larger loans through our verified P2P lending network
          </motion.p>
        </div>

        <div className="p2p-container">

          {/* ── Calculator ── */}
          <motion.div
            className="p2p-calculator"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3>Loan Calculator</h3>

            <div className="calculator-input">
              <label>Loan Amount: ₹{loanAmount.toLocaleString()}</label>
              <input type="range" min="10000" max="200000" step="10000"
                value={loanAmount} onChange={e => setLoanAmount(parseInt(e.target.value))}
                className="loan-slider" />
              <div className="slider-labels"><span>₹10,000</span><span>₹2,00,000</span></div>
            </div>

            <div className="calculator-input">
              <label>Repayment: {repaymentTerm} M</label>
              <input type="range" min="3" max="60" step="1"
                value={repaymentTerm} onChange={e => setRepaymentTerm(parseInt(e.target.value))}
                className="term-slider" />
              <div className="slider-labels"><span>3 months</span><span>60 months</span></div>
            </div>

            <div className="calculator-input">
              <label>Interest Rate: {interestRate}% p.a.</label>
              <input type="range" min="8" max="36" step="1"
                value={interestRate} onChange={e => setInterestRate(parseFloat(e.target.value))}
                className="rate-slider" />
              <div className="slider-labels"><span>8%</span><span>36%</span></div>
            </div>

            <div className="total-loan-amount">
              <h4>Total Loan Amount</h4>
              <div className="amount">
                <span className="amount-symbol">₹</span>
                {loanAmount.toLocaleString()}
              </div>
              <p className="subtext">Adjusted based on your Rupayya Score</p>
            </div>

            <div className="loan-details">
              <div className="detail-item">
                <span>Interest Rate</span>
                <strong>{interestRate}% p.a.</strong>
              </div>
              <div className="detail-item">
                <span>Tenure</span>
                <strong>{repaymentTerm} M</strong>
              </div>
              <div className="detail-item">
                <span>Monthly EMI</span>
                <strong>₹{calculateEMI().toLocaleString()}</strong>
              </div>
            </div>

            <div className="button-container">
              <motion.button
                className="apply-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
              >
                Check Eligibility on Our App
              </motion.button>
            </div>
          </motion.div>

          {/* ── Benefits ── */}
          <motion.div
            className="p2p-benefits"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <h3>Why Choose Rupayya P2P?</h3>
            {benefits.map((b, idx) => (
              <motion.div
                key={idx}
                className="benefit-item"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.07 }}
                whileHover={{ x: 4 }}
              >
                <div className="benefit-icon">{b.icon}</div>
                <div>
                  <h4>{b.title}</h4>
                  <p>{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* ── Interest comparison ── */}
        <motion.div
          className="interest-comparison"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h3>Compare Interest Rates</h3>
          <div className="comparison-chart">
            {comparisons.map((item, idx) => (
              <div key={idx} className="comparison-item">
                <h4>{item.name}</h4>
                <div className="interest-rate">{item.rate}</div>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── P2P Process ── */}
        <div className="p2p-process">
          <h3>How P2P Lending Works</h3>
          <div className="process-steps">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                className="process-step"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                whileHover={{ y: -4 }}
              >
                <div className="step-number">{step.number}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default P2PLending;
